import React, { useCallback, useEffect, useState } from "react";
import Lottie from "lottie-react";
import useLottieRef from "hooks/useLottieRef";
import useLayoutUtils from "hooks/useLayoutUtils";

import introDesktopAnimation from "assets/lotties/footer/footer_desktop_intro.json";
import loopDesktopAnimation from "assets/lotties/footer/footer_desktop_loop.json";
import introMobileAnimation from "assets/lotties/footer/footer_mobile_intro.json";
import loopMobileAnimation from "assets/lotties/footer/footer_mobile_loop.json";
import introLogoDesktopAnimation from "assets/lotties/footer/logo_desktop_intro.json";
import loopLogoDesktopAnimation from "assets/lotties/footer/logo_desktop_loop.json";
import introLogoMobileAnimation from "assets/lotties/footer/logo_mobile_intro.json";
import loopLogoMobileAnimation from "assets/lotties/footer/logo_mobile_loop.json";

import exitIntro from "assets/lotties/footerButtons/exitbutton_intro.json";
import exitLoop from "assets/lotties/footerButtons/exitbutton_loop.json";
import exitGlow from "assets/lotties/footerButtons/exitbutton_glow.json";

import homeIntro from "assets/lotties/footerButtons/homebutton_intro_safari.json";
import homeLoop from "assets/lotties/footerButtons/homebutton_loop_safari.json";
import homeGlow from "assets/lotties/footerButtons/homebutton_glow.json";

import infoIntro from "assets/lotties/footerButtons/infobutton_intro_safari.json";
import infoLoop from "assets/lotties/footerButtons/infobutton_loop_safari.json";
import infoGlow from "assets/lotties/footerButtons/infobutton_glow.json";

import useIsMobile from "hooks/useIsMobile";
import { useLayoutContext } from "layout/layout-provider";
import useExternalLink from "../hooks/useExternalLink";
import FooterButton from "./footerButton";
import useNavigation from "../hooks/useNavigation";
import { useScribeContext } from "contexts/scribe/scribe-provider";
import CustomLottie from "../components/custom-lottie";
import { useLocation } from "react-router-dom";
import { useQueryParam } from "../contexts/QueryParamContext";

const footerWidth = 1920;
const footerWidthWithNoSpace = 720;
const mobileFooterWidth = 430;
const footerHeight = 152;
const homeButtonX = 780;
const infoButtonX = 1140;
const mobileXOffset = 600;
const buttonY = 120;
const buttonSize = 100;
const websiteButtonSize = 115;

const museumWebsite = "https://www.museoegizio.it/";

const Footer: React.FC = () => {
  /*const { externalLinksDisabled } = useQueryParam();*/

  const isExternalLinksDisabled =
    localStorage.getItem("external_links_disabled") ?? "false";

  const [canHomeButtonBeClicked, setCanHomeButtonBeClicked] =
    React.useState<boolean>(true);
  const [introHidden, setIntroHidden] = React.useState<boolean>(false);
  const {
    windowWidth,
    setWindowWidth,
    setFooterLoaded,
    firstLayerHidden,
    secondLayerHidden,
    setFirstLayerHidden,
    setSecondLayerHidden,
    setShowQrAnimation,
    setCardVisibilityFade,
    setCardReady,
    setShouldInfoFadeOut,
  } = useLayoutContext();

  const link = useExternalLink(museumWebsite);

  const isMobile = useIsMobile();
  const {
    reset: resetScribe,
    setSwitchToQrCodeScribe,
    setShowQrCode,
    setInitialized,
    setHideScribaIntro,
  } = useScribeContext();
  const { bodyHeight } = useLayoutUtils();

  const introCompleted = useCallback(() => {
    setIntroHidden(true);
    setFooterLoaded(true);
  }, [setIntroHidden, setFooterLoaded]);

  const { navigateToHome } = useNavigation();
  const [clickCounter, setClickCounter] = useState(0);
  const [scale, setScale] = React.useState<number>(0);

  /*
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [setWindowWidth]);*/

  useEffect(() => {
    if (!introHidden) return;

    const retrievedFooterWidth =
      document.querySelector(".footer")?.clientWidth ??
      document.body.clientWidth;

    setScale(
      retrievedFooterWidth / (isMobile ? footerWidthWithNoSpace : footerWidth),
    );
  }, [introHidden, isMobile]);

  const openInfoGeneratedPage = () => {
    setFirstLayerHidden(true);
    setSecondLayerHidden(false);
  };

  const closeInfoGeneratedPage = () => {
    setFirstLayerHidden(false);
    setSecondLayerHidden(true);
    setShouldInfoFadeOut(true);
  };

  const goHome = useCallback(() => {
    setShowQrCode(false);


    let timerId: NodeJS.Timeout;
    const cleanup = () => clearTimeout(timerId);

    setCardVisibilityFade(false);

    timerId = setTimeout(() => {
      resetScribe();
      setInitialized(false);
      setSwitchToQrCodeScribe(false);
      setHideScribaIntro(false);
      setCardReady(false);

      navigateToHome();
      setCanHomeButtonBeClicked(true);
    }, 1000);

    return cleanup;
  }, [
    navigateToHome,
    resetScribe,
    setCardVisibilityFade,
    setHideScribaIntro,
    setInitialized,
    setShowQrCode,
    setSwitchToQrCodeScribe,
  ]);

  return (
    <>
      {!introHidden && (
        <div
          className="relative width-100 height-100"
          style={{ zIndex: "100" }}
        >
          <CustomLottie
            animationData={
              isMobile ? introMobileAnimation : introDesktopAnimation
            }
            loop={false}
            autoplay={true}
            className={`lottie-animation footer footer${
              isMobile ? "mobile" : "desktop"
            }`}
            onComplete={introCompleted}
            speed={1}
          />
        </div>
      )}
      {introHidden && (
        <div
          className="relative width-100 height-100"
          style={{ zIndex: "100" }}
        >
          {!firstLayerHidden && secondLayerHidden && (
            <>
              <FooterButton
                canBeClicked={canHomeButtonBeClicked}
                glowAnimationData={homeGlow}
                introAnimationData={homeIntro}
                loopAnimationData={homeLoop}
                buttonType={"home"}
                onClick={() => {
                  setCanHomeButtonBeClicked(false);
                  goHome();
                }}
                className="absolute"
                style={{
                  left:
                    (homeButtonX -
                      buttonSize / 2 -
                      (isMobile ? mobileXOffset : 0)) *
                    scale,
                  top: (buttonY - buttonSize / 2) * scale,
                  width: buttonSize * scale,
                  zIndex: 101,
                }}
              />
              <FooterButton
                canBeClicked={true}
                glowAnimationData={infoGlow}
                introAnimationData={infoIntro}
                loopAnimationData={infoLoop}
                buttonType={"info"}
                onClick={openInfoGeneratedPage}
                className="absolute"
                style={{
                  left:
                    (infoButtonX -
                      buttonSize / 2 -
                      (isMobile ? mobileXOffset : 0)) *
                    scale,
                  top: (buttonY - buttonSize / 2) * scale,
                  width: buttonSize * scale,
                  zIndex: 101,
                }}
              />
            </>
          )}
          {firstLayerHidden && !secondLayerHidden && (
            <>
              <FooterButton
                canBeClicked={true}
                glowAnimationData={exitGlow}
                introAnimationData={exitIntro}
                loopAnimationData={exitLoop}
                buttonType={"exit"}
                onClick={closeInfoGeneratedPage}
                className="absolute"
                style={{
                  left:
                    (infoButtonX -
                      buttonSize / 2 -
                      (isMobile ? mobileXOffset : 0)) *
                    scale,
                  top: (buttonY - buttonSize / 2) * scale,
                  width: buttonSize * scale,
                  zIndex: 101,
                }}
              />
            </>
          )}

          {/*{!externalLinksDisabled && (*/}
          {isExternalLinksDisabled === "false" && (
            <>
              <button
                id="lottie-button"
                style={{
                  position: "absolute",
                  top: 0,
                  left: `calc((${
                    footerWidth - (isMobile ? mobileXOffset * 2 : 0)
                  }px - ${websiteButtonSize}px) * ${scale / 2})`,
                  width: `${websiteButtonSize * scale}px`,
                  height: `${websiteButtonSize * scale}px`,
                  opacity: 0,
                  cursor: "pointer",
                  zIndex: 110,
                  borderRadius: "25px",
                  border: 0,
                }}
                onClick={link}
              />
            </>
          )}
          <CustomLottie
            animationData={
              isMobile ? loopMobileAnimation : loopDesktopAnimation
            }
            loop={true}
            autoplay={true}
            className={`lottie-animation footer footer${
              isMobile ? "mobile" : "desktop"
            }`}
            style={{ zIndex: "100" }}
            speed={1}
          />
        </div>
      )}
    </>
  );
};

export default Footer;
